// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: the agent's questions (T3's user-input request: Claude's AskUserQuestion, Codex's
// request_user_input), shown above the composer so they pop up wherever the thread is scrolled.
// One question at a time with "1 of 2", options as numbered rows (number keys pick them), a
// written answer when the agent allows one, and Send once each question has an answer. Answers
// go as the option value, a list of values for multi-select, or the written text, as T3 sends them.
// Dismiss closes it unanswered (bridges 0.36.0+), no confirmation: one the turn waits on says
// "Dismiss and stop", since T3 can only stop that turn. The parent keys the card by request, so a
// new question starts fresh.

import askStyles from '@app/features/konvo_agents/components/AgentAsk.module.css';
import {AgentAskButton} from '@app/features/konvo_agents/components/AgentAskPicker';
import {RunningRing} from '@app/features/konvo_agents/components/AgentBits';
import {PendingFiles, usePendingFiles} from '@app/features/konvo_agents/components/AgentComposerFiles';
import styles from '@app/features/konvo_agents/components/AgentQuestion.module.css';
import {useThreadShare} from '@app/features/konvo_agents/components/AgentShareContext';
import {agentErrorMessage} from '@app/features/konvo_agents/components/AgentsCopy';
import {AgentTime} from '@app/features/konvo_agents/components/AgentTime';
import AgentAsks from '@app/features/konvo_agents/state/AgentAsks';
import {askForAnswers, firstName, unsigned, waitingAsks} from '@app/features/konvo_agents/state/AgentAsksModel';
import Agents from '@app/features/konvo_agents/state/Agents';
import {canDismissQuestion, canSend} from '@app/features/konvo_agents/state/AgentsModel';
import {fitComposerHeight} from '@app/features/platform/utils/ComposerHeight';
import * as ToastCommands from '@app/features/ui/commands/ToastCommands';
import type {
	AgentAnswer,
	AgentAnswers,
	AgentAsk,
	AgentMachineView,
	AgentQuestion,
	AgentQuestionRequest,
	AgentQuestionUploads,
} from '@konvo/schema/src/domains/konvo/Agents';
import {AGENT_UPLOAD_IMAGE_TYPES} from '@konvo/schema/src/domains/konvo/Agents';
import {Trans, useLingui} from '@lingui/react/macro';
import {
	IconArrowUp,
	IconCheck,
	IconChevronLeft,
	IconCircleCheck,
	IconMessageQuestion,
	IconPhotoPlus,
} from '@tabler/icons-react';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useEffect, useRef, useState} from 'react';

export type Draft = {selected: Array<string>; custom: string};
export const EMPTY_DRAFT: Draft = {selected: [], custom: ''};
const EMPTY = EMPTY_DRAFT;

export const optionValue = (option: AgentQuestion['options'][number]) => option.value ?? option.label;

/** The answer a draft gives, as T3's own card works it out: a written answer wins, else the picks. */
export function draftAnswer(question: AgentQuestion, draft: Draft | undefined): string | Array<string> | null {
	const custom = question.allowCustomAnswer ? (draft?.custom.trim() ?? '') : '';
	if (custom) return custom;
	const values = question.options.map(optionValue);
	const picked = (draft?.selected ?? []).filter((value) => values.includes(value));
	if (question.multiSelect) return picked.length ? picked : null;
	return picked[0] ?? null;
}

/** All answers, or null while one is missing. */
export function draftAnswers(
	questions: ReadonlyArray<AgentQuestion>,
	drafts: Record<string, Draft>,
): AgentAnswers | null {
	const out: AgentAnswers = {};
	for (const question of questions) {
		const answer = draftAnswer(question, drafts[question.id]);
		if (answer === null) return null;
		out[question.id] = answer;
	}
	return out;
}

/** "Question" is T3's placeholder header; anything else is worth showing. */
export const realHeader = (header: string) =>
	header.trim() && header.trim().toLowerCase() !== 'question' ? header.trim() : null;

export const AgentQuestionCard = observer(
	({
		machine,
		threadId,
		request,
		phone,
		className,
	}: {
		machine: AgentMachineView;
		threadId: string;
		request: AgentQuestionRequest;
		phone: boolean;
		className?: string;
	}) => {
		const {i18n, t} = useLingui();
		const [index, setIndex] = useState(0);
		const [drafts, setDrafts] = useState<Record<string, Draft>>({});
		const [sending, setSending] = useState(false);
		const [dismissing, setDismissing] = useState(false);
		const files = usePendingFiles();
		const [photoQuestions, setPhotoQuestions] = useState<Record<string, string>>({});
		const [addingPhotos, setAddingPhotos] = useState(false);
		const photoInput = useRef<HTMLInputElement>(null);
		const submission = useRef(false);
		const allowed = canSend(machine);
		const dismissable = canDismissQuestion(machine);
		const busy = sending || dismissing || addingPhotos;
		const questions = request.questions;
		const total = questions.length;
		const step = Math.min(index, total - 1);
		const question = questions[step]!;
		const draft = drafts[question.id] ?? EMPTY;
		const photosAllowed = machine.tools[0]?.can.questionFiles === true;
		const photos = files.files.filter((file) => photoQuestions[file.key] === question.id);
		// A photo can be the whole written answer, while option-only questions still require a pick.
		const withPhotos = {...drafts};
		for (const q of questions) {
			if (
				q.allowCustomAnswer &&
				draftAnswer(q, drafts[q.id]) === null &&
				files.files.some((file) => photoQuestions[file.key] === q.id)
			)
				withPhotos[q.id] = {selected: [], custom: t`See attached photos.`};
		}
		const answer = draftAnswer(question, withPhotos[question.id]);
		const last = step >= total - 1;
		const header = realHeader(question.header);
		const machineName = machine.name;
		const position = step + 1;
		const waiting = waitingAsks(AgentAsks.forThread(machine.id, threadId), request.requestId);
		// A thread shared with you: asking a colleague stays the owner's.
		const teammate = useThreadShare()?.role === 'teammate';
		useEffect(() => {
			if (!teammate) void AgentAsks.loadThread(machine.id, threadId);
		}, [machine.id, threadId, teammate]);

		const update = (next: Draft) => setDrafts((current) => ({...current, [question.id]: next}));

		const pick = (value: string) => {
			if (question.multiSelect) {
				const selected = draft.selected.includes(value)
					? draft.selected.filter((v) => v !== value)
					: [...draft.selected, value];
				update({selected, custom: ''});
				return;
			}
			update({selected: [value], custom: ''});
		};

		const send = async () => {
			const answers = draftAnswers(questions, withPhotos);
			if (!answers || busy || submission.current) return;
			submission.current = true;
			setSending(true);
			try {
				const uploads = await files.uploads();
				const attachmentsByQuestionId: AgentQuestionUploads = {};
				for (const [i, upload] of uploads.entries()) {
					const id = photoQuestions[files.files[i]!.key]!;
					(attachmentsByQuestionId[id] ??= []).push(upload);
				}
				await Agents.answer(
					machine.id,
					threadId,
					request.requestId,
					answers,
					...(uploads.length ? [attachmentsByQuestionId] : []),
				);
				files.clear();
			} catch (error) {
				submission.current = false;
				ToastCommands.error(agentErrorMessage(i18n, error));
			} finally {
				setSending(false);
			}
		};

		const dismiss = async () => {
			if (busy) return;
			setDismissing(true);
			try {
				// The card leaves at once; it comes back if the machine says no.
				await Agents.dismiss(machine.id, threadId, request.requestId);
			} catch (error) {
				ToastCommands.error(agentErrorMessage(i18n, error));
				setDismissing(false);
			}
		};

		const advance = () => {
			if (answer === null || busy || submission.current) return;
			if (last) void send();
			else setIndex(step + 1);
		};

		// Number keys pick options while the card has focus and the written answer is empty, as in T3.
		const onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
			if (!allowed || busy || event.metaKey || event.ctrlKey || event.altKey) return;
			const typing = event.target instanceof HTMLTextAreaElement && event.target.value !== '';
			if (!typing && /^[1-9]$/.test(event.key)) {
				const option = question.options[Number(event.key) - 1];
				if (option) {
					event.preventDefault();
					pick(optionValue(option));
				}
			}
		};

		const options = question.options;
		const writtenOnly = options.length === 0;
		return (
			<div
				className={clsx(styles.card, phone && styles.cardPhone, className)}
				role="group"
				aria-label={t`The agent asks`}
				onKeyDown={onKeyDown}
				data-flx="konvo-agents.thread.question"
				// Voice mode outlines the question Konvo is answering (VoiceAgentsPointer).
				data-agent-request={request.requestId}
			>
				<div className={styles.head}>
					<span className={styles.headIcon}>
						<IconMessageQuestion size={17} stroke={1.7} />
					</span>
					<span className={styles.kicker}>{header ?? <Trans>The agent asks</Trans>}</span>
					{total > 1 && (
						<span className={styles.step} data-flx="konvo-agents.thread.question.step">
							<Trans comment="Agents: which of the agent's questions is showing, e.g. 1 of 2.">
								{position} of {total}
							</Trans>
						</span>
					)}
					<AgentTime at={request.createdAt} className={styles.headTime} flx="konvo-agents.thread.question.time" />
				</div>
				{/* Scrolls on its own when a long question meets a small screen (phone with the keyboard
				    up), so the answer box and buttons below stay in view. */}
				<div className={styles.body} data-flx="konvo-agents.thread.question.body">
					<p className={styles.question} data-flx="konvo-agents.thread.question.text">
						{question.question}
					</p>
					{question.multiSelect && options.length > 0 && allowed && (
						<span className={styles.hint}>
							<Trans>Pick one or more.</Trans>
						</span>
					)}
					{options.length > 0 && (
						<div
							className={styles.options}
							role="group"
							aria-label={question.question}
							data-flx="konvo-agents.thread.question.options"
						>
							{options.map((option, i) => {
								const value = optionValue(option);
								const on = draft.selected.includes(value) && !draft.custom.trim();
								return (
									<button
										key={value}
										type="button"
										aria-pressed={on}
										className={clsx(styles.option, on && styles.optionOn)}
										onClick={(event) => {
											const selection = document.getSelection();
											if (!selection?.toString() || !selection.containsNode(event.currentTarget, true)) pick(value);
										}}
										disabled={!allowed || busy}
										data-flx="konvo-agents.thread.question.option"
									>
										<span className={clsx(styles.key, question.multiSelect && styles.keySquare)}>
											{on ? <IconCheck size={13} stroke={2.4} /> : i + 1}
										</span>
										<span className={styles.optionText}>
											<span className={styles.optionLabel}>{option.label}</span>
											{option.description && <span className={styles.optionDescription}>{option.description}</span>}
										</span>
									</button>
								);
							})}
						</div>
					)}
					<PendingFiles
						files={photos}
						onRemove={busy ? undefined : files.remove}
						dataFlx="konvo-agents.thread.question.photos"
					/>
				</div>
				{!allowed ? (
					<span className={styles.note}>
						<Trans>This machine is read-only in Konvo. Answer it in the agent's app on {machineName}.</Trans>
					</span>
				) : (
					<div className={styles.foot}>
						{question.allowCustomAnswer && (
							<textarea
								className={clsx(styles.written, writtenOnly && styles.writtenMain)}
								rows={1}
								value={draft.custom}
								placeholder={writtenOnly ? t`Write your answer…` : t`Or write your own answer…`}
								aria-label={t`Your answer`}
								onChange={(event) => {
									const custom = event.target.value;
									// A written answer replaces the picked option, as in T3.
									update({selected: custom.trim() ? [] : draft.selected, custom});
									fitComposerHeight(event.target);
								}}
								onKeyDown={(event) => {
									if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing && !phone) {
										event.preventDefault();
										advance();
									}
								}}
								disabled={busy}
								data-flx="konvo-agents.thread.question.written"
							/>
						)}
						<div className={styles.actions}>
							{photosAllowed && (
								<>
									<input
										ref={photoInput}
										type="file"
										multiple
										accept={AGENT_UPLOAD_IMAGE_TYPES.join(',')}
										hidden
										onChange={(event) => {
											const incoming = Array.from(event.target.files ?? []).filter((file) =>
												(AGENT_UPLOAD_IMAGE_TYPES as ReadonlyArray<string>).includes(file.type),
											);
											event.target.value = '';
											const id = question.id;
											setAddingPhotos(true);
											void files
												.add(incoming)
												.then((added) =>
													setPhotoQuestions((current) => ({
														...current,
														...Object.fromEntries(added.map((file) => [file.key, id])),
													})),
												)
												.catch((error) => ToastCommands.error(agentErrorMessage(i18n, error)))
												.finally(() => setAddingPhotos(false));
										}}
										data-flx="konvo-agents.thread.question.photo-input"
									/>
									<button
										type="button"
										className={styles.back}
										aria-label={t`Add photos`}
										title={t`Add photos`}
										disabled={busy || files.full}
										onClick={() => photoInput.current?.click()}
										data-flx="konvo-agents.thread.question.add-photos"
									>
										<IconPhotoPlus size={18} stroke={1.9} />
									</button>
								</>
							)}
							{dismissable && (
								<button
									type="button"
									className={styles.dismiss}
									onClick={() => void dismiss()}
									disabled={busy}
									title={
										request.blocking
											? t({
													message: 'The agent is waiting on this answer, so this stops its turn.',
													comment: "Agents: tooltip on Dismiss and stop, on an agent's question its turn waits on.",
												})
											: t({
													message: 'Close this question without answering. The agent is not told.',
													comment: "Agents: tooltip on Dismiss, on an agent's question.",
												})
									}
									data-flx="konvo-agents.thread.question.dismiss"
								>
									{request.blocking ? (
										<Trans comment="Agents: quiet button on an agent's question its turn waits on: close it unanswered and stop the turn.">
											Dismiss and stop
										</Trans>
									) : (
										<Trans comment="Agents: quiet button on an agent's question: close it without answering.">
											Dismiss
										</Trans>
									)}
								</button>
							)}
							{!teammate && (
								<AgentAskButton
									machineId={machine.id}
									threadId={threadId}
									requestId={request.requestId}
									waiting={waiting}
									phone={phone}
									disabled={busy}
								/>
							)}
							{step > 0 && (
								<button
									type="button"
									className={styles.back}
									onClick={() => setIndex(step - 1)}
									disabled={busy}
									aria-label={t`Previous question`}
									title={t`Previous question`}
									data-flx="konvo-agents.thread.question.back"
								>
									<IconChevronLeft size={18} stroke={1.9} />
								</button>
							)}
							<button
								type="button"
								className={styles.send}
								onClick={advance}
								disabled={answer === null || busy}
								data-flx="konvo-agents.thread.question.send"
							>
								{sending ? (
									<RunningRing size={14} />
								) : last ? (
									<>
										<Trans>Send answer</Trans>
										<IconArrowUp size={16} stroke={2} />
									</>
								) : (
									<Trans>Next</Trans>
								)}
							</button>
						</div>
					</div>
				)}
			</div>
		);
	},
);

/** A message that is the person's answer: each question, quietly, with the answer under it. */
export function AnsweredQuestions({answers, ask}: {answers: ReadonlyArray<AgentAnswer>; ask?: AgentAsk | null}) {
	if (ask) answers = unsigned(answers, ask.personName);
	const personName = ask?.personName ?? '';
	const ownerName = ask ? firstName(ask.ownerName) : '';
	return (
		<div className={styles.answered} data-flx="konvo-agents.thread.answered">
			{ask ? (
				<span className={askStyles.byRow} data-flx="konvo-agents.thread.answered-by">
					<span className={askStyles.byLabel}>
						<IconCircleCheck size={15} stroke={1.9} />
						<Trans comment="Agents: a colleague answered the agent's question, e.g. Answered by Lerato Mokoena.">
							Answered by {personName}
						</Trans>
					</span>
					<span className={askStyles.byMeta}>
						<Trans comment="Agents: who sent the question to the colleague, e.g. asked by JP; a time follows.">
							asked by {ownerName}
						</Trans>
						{ask.answeredAt && <span aria-hidden>·</span>}
						<AgentTime at={ask.answeredAt} flx="konvo-agents.thread.answered-by.time" />
					</span>
				</span>
			) : (
				<span className={styles.answeredLabel}>
					<IconMessageQuestion size={14} stroke={1.7} />
					<Trans comment="Agents: label on a message that answers the agent's questions.">Answered</Trans>
				</span>
			)}
			{answers.map((pair, i) => (
				<div key={i} className={styles.pair}>
					<span className={styles.pairQuestion}>{pair.question}</span>
					<span className={styles.pairAnswer}>{pair.answer}</span>
				</div>
			))}
		</div>
	);
}

/** An answer in the thread, with who answered it when a colleague did (Ask a colleague). */
export const AnsweredItem = observer(
	({
		machineId,
		threadId,
		answers,
		createdAt,
	}: {
		machineId: string;
		threadId: string;
		answers: ReadonlyArray<AgentAnswer>;
		createdAt: string;
	}) => {
		const ask = askForAnswers(AgentAsks.forThread(machineId, threadId), answers, createdAt);
		return <AnsweredQuestions answers={answers} ask={ask} />;
	},
);
